import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const Navbar = () => {
  const [open, setOpen] = useState(false);

  const menuButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <nav aria-label="Navigation principale" className="landing-nav fixed inset-x-0 top-4 z-50 px-4">
      {/* Liquid glass : forte translucidité + blur saturé, liseré spéculaire en haut,
          reflet diagonal — le contenu de la page se devine à travers la capsule. */}
      <div
        className="container relative flex h-14 max-w-6xl items-center justify-between overflow-hidden rounded-2xl border px-5"
        style={{ background: "rgba(255,255,255,0.96)", borderColor: "#E2E7EF", backdropFilter: "blur(16px)", boxShadow: "0 4px 24px -12px rgba(20,20,43,0.12)" }}
      >
        <a href="/" aria-label="CoachOS, accueil" className="flex shrink-0 items-center gap-2">
          <Logo size={26} className="text-[#2563EB]" />
          <span className="text-lg font-extrabold tracking-tight text-[#0e1220]">CoachOS</span>
        </a>

        <div className="hidden lg:flex items-center gap-6">
          <a href="/generateur-programme" className="text-sm text-[#0e1220]/60 hover:text-[#0e1220] transition-colors">Essai gratuit</a>
          <a href="/#features" className="text-sm text-[#0e1220]/60 hover:text-[#0e1220] transition-colors">Fonctionnalités</a>
          <a href="/#apps" className="text-sm text-[#0e1220]/60 hover:text-[#0e1220] transition-colors">App</a>
          <a href="/#pricing" className="text-sm text-[#0e1220]/60 hover:text-[#0e1220] transition-colors">Tarif</a>
          <a href="/blog" className="text-sm text-[#0e1220]/60 hover:text-[#0e1220] transition-colors">Blog</a>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild className="text-[#0e1220]/70 hover:text-[#0e1220] hover:bg-[#eef0f6]">
            <a
              href={`${import.meta.env.VITE_APP_URL ?? "https://app.coach-os.eu"}/login`}
              onClick={() =>
                track("login_cta_clicked", {
                  location: "navbar",
                })
              }
            >
              Connexion
            </a>
          </Button>
          <Button
            size="sm"
            className="rounded-lg bg-[#2563EB] text-white hover:bg-[#1D4ED8] px-5"
            onClick={() => {
              track("landing_cta_clicked", {
                cta: "start_now",
                location: "navbar",
                destination: "pricing",
              });
              window.location.assign("/#pricing");
            }}
          >
            Voir les offres
          </Button>
        </div>

        <button ref={menuButton} type="button" aria-controls="mobile-navigation" className="grid h-11 w-11 place-items-center rounded-lg lg:hidden text-[#0e1220]" onClick={() => setOpen(!open)} aria-label={open ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={open}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        /* Panneau mobile : même capsule liquid glass que la barre, liens
           empilés avec hairlines et CTA pill — lisible par-dessus le contenu. */
        <div
          id="mobile-navigation"
          className="container max-h-[calc(100dvh-100px)] overflow-y-auto mt-2 max-w-6xl rounded-2xl border px-5 py-2 lg:hidden"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.98))",
            borderColor: "#e7e9f1",
            backdropFilter: "blur(22px) saturate(150%)",
            WebkitBackdropFilter: "blur(22px) saturate(150%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 24px 60px -20px rgba(16,20,40,0.28)",
          }}
        >
          {[
            ["/generateur-programme", "Essai gratuit"],
            ["/#features", "Fonctionnalités"],
            ["/#apps", "App"],
            ["/#pricing", "Tarif"],
            ["/blog", "Blog"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="block border-b border-[#e7e9f1] py-3.5 text-[15px] font-medium text-[#0e1220]/85"
              onClick={() => setOpen(false)}
            >
              {label}
            </a>
          ))}
          <a
            href={`${import.meta.env.VITE_APP_URL ?? "https://app.coach-os.eu"}/login`}
            className="block py-3.5 text-[15px] font-medium text-[#0e1220]/85"
            onClick={() => track("login_cta_clicked", { location: "mobile_navbar" })}
          >
            Connexion
          </a>
          <button
            className="mb-3 mt-1 flex w-full items-center justify-center rounded-full bg-[#14142B] py-3 font-semibold text-white shadow-[0_8px_20px_rgba(20,20,43,0.16)]"
            onClick={() => {
              track("landing_cta_clicked", {
                cta: "start_now",
                location: "mobile_navbar",
                destination: "pricing",
              });
              setOpen(false);
              window.location.assign("/#pricing");
            }}
          >
            Voir les offres
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
